import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Pagination, Tag, Tile, Select, MenuItem, MultiPanelTile, Panel, PanelAction, Tooltip, Timeline, Dialog, Tree, TreeItem } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { niiService } from '../../services/niiService';
import { GplFinding } from './types/nii.types';

const STATUS_TAG: Record<string, { label: string; color: string }> = {
  mismatch: { label: 'Mismatch', color: 'red' },
  param_set_new: { label: 'Param Set', color: 'orange' },
  param_cleared: { label: 'Param Cleared', color: 'yellow' },
  feature_activate: { label: 'Feature Activate', color: 'blue' },
  feature_deactivate: { label: 'Feature Deactivate', color: 'purple' }
};

const GplNodeReport: React.FC = () => {
  const { runId: _runId, reportId: urlReportId } = useParams<{ runId: string; reportId: string }>();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as { auditName?: string; nodeName?: string; auditResult?: string; gplVersion?: string } | null;

  // reportId comes directly from URL params — updates reactively on navigation
  const reportId = urlReportId;

  // Fallback: fetch run metadata only if auditName not passed via nav state
  const { data: run } = useQuery({
    queryKey: ['gpl-audit-run', _runId],
    queryFn: () => niiService.getRun(_runId!),
    enabled: !!_runId && !navState?.auditName,
    refetchOnWindowFocus: false
  });

  // Show Older / Show Less
  const INITIAL_SHOW_COUNT = 5;
  const [showAll, setShowAll] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [gplStatus, setGplStatus] = useState('');
  const [category, setCategory] = useState('');
  const [moClass, setMoClass] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Sorting
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  // View mode
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');

  // Finding detail dialog
  const [selectedFinding, setSelectedFinding] = useState<GplFinding | null>(null);

  // Left panel (audit runs timeline)
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);

  // Build query string
  const queryString = useMemo(() => {
    let q = `page=${page}&limit=${limit}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    if (gplStatus) q += `&gplStatus=${encodeURIComponent(gplStatus)}`;
    if (category) q += `&category=${encodeURIComponent(category)}`;
    if (moClass) q += `&moClass=${encodeURIComponent(moClass)}`;
    if (sortBy) q += `&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    q += '&filterList=status,category,mo_class_name';
    return q;
  }, [page, limit, search, gplStatus, category, moClass, sortBy, sortOrder]);

  // Fetch report content (table view — paginated)
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['gpl-node-report', reportId, queryString],
    queryFn: () => niiService.getGplReportContent(reportId!, queryString),
    enabled: !!reportId,
    refetchOnWindowFocus: false
  });

  // Derive nodeName / gplVersion. On a hard refresh, location.state (navState)
  // is lost, so fall back to the report's own documentMetadata (fetched from
  // reportId, which survives in the URL). Without this fallback the audit
  // history query below stays disabled and the timeline shows "No previous
  // audit runs found".
  const docMeta = reportData?.documentMetadata;
  const nodeName = navState?.nodeName || docMeta?.summary?.node_name || '';
  const gplVersion = navState?.gplVersion || run?.gplVersion || docMeta?.gpl_version || '';

  // Fetch audit history for the timeline
  const { data: auditHistory = [] } = useQuery({
    queryKey: ['gpl-audit-history', nodeName, gplVersion],
    queryFn: () => niiService.getNodeAuditHistory(nodeName, gplVersion),
    enabled: !!nodeName && !!gplVersion,
    refetchOnWindowFocus: false
  });

  const visibleHistory = showAll ? auditHistory : auditHistory.slice(0, INITIAL_SHOW_COUNT);

  // Tree view: accumulate findings across pages with "Show More"
  const TREE_PAGE_SIZE = 50;
  const [treePage, setTreePage] = useState(1);
  const [treeFindings, setTreeFindings] = useState<GplFinding[]>([]);
  const [treeTotal, setTreeTotal] = useState(0);
  const [treeLoadingMore, setTreeLoadingMore] = useState(false);

  // Build tree query string (separate from table — uses fixed page size, applies same filters)
  const treeQueryString = useMemo(() => {
    let q = `page=1&limit=${TREE_PAGE_SIZE}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    if (gplStatus) q += `&gplStatus=${encodeURIComponent(gplStatus)}`;
    if (category) q += `&category=${encodeURIComponent(category)}`;
    if (moClass) q += `&moClass=${encodeURIComponent(moClass)}`;
    return q;
  }, [search, gplStatus, category, moClass]);

  // Initial tree fetch (page 1) — only when tree view is active
  const { data: treeInitialData } = useQuery({
    queryKey: ['gpl-node-report-tree', reportId, treeQueryString],
    queryFn: () => niiService.getGplReportContent(reportId!, treeQueryString),
    enabled: !!reportId && viewMode === 'tree',
    refetchOnWindowFocus: false
  });

  // Sync accumulated tree state from the current tree query (page 1). This runs
  // on entering tree view, and whenever filters change (treeQueryString) — for
  // both applying AND clearing filters. Syncing directly from treeInitialData
  // (rather than clearing then relying on a separate re-seed) is important:
  // when a filter is cleared, treeQueryString reverts to a value whose query is
  // already cached, so treeInitialData's reference does not change. Depending on
  // treeQueryString here guarantees we re-sync from the cached data in that case
  // instead of leaving the tree empty.
  useEffect(() => {
    if (viewMode !== 'tree') return;
    if (treeInitialData) {
      setTreeFindings(treeInitialData.data);
      setTreeTotal(treeInitialData.total);
      setTreePage(1);
    } else {
      // Query for this filter set not resolved yet — clear until it arrives.
      setTreeFindings([]);
      setTreePage(1);
      setTreeTotal(0);
    }
  }, [treeInitialData, viewMode, treeQueryString]);

  // Reset accumulation whenever we leave tree view.
  useEffect(() => {
    if (viewMode !== 'tree') {
      setTreeFindings([]);
      setTreePage(1);
      setTreeTotal(0);
    }
  }, [viewMode]);

  const handleShowMoreTree = async () => {
    if (!reportId) return;
    setTreeLoadingMore(true);
    const nextPage = treePage + 1;
    let q = `page=${nextPage}&limit=${TREE_PAGE_SIZE}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    if (gplStatus) q += `&gplStatus=${encodeURIComponent(gplStatus)}`;
    if (category) q += `&category=${encodeURIComponent(category)}`;
    if (moClass) q += `&moClass=${encodeURIComponent(moClass)}`;
    try {
      const moreData = await niiService.getGplReportContent(reportId, q);
      setTreeFindings((prev) => [...prev, ...moreData.data]);
      setTreePage(nextPage);
      setTreeTotal(moreData.total);
    } finally {
      setTreeLoadingMore(false);
    }
  };

  const findings = reportData?.data || [];
  const totalPages = reportData?.totalPages || 0;
  const total = reportData?.total || 0;
  const summary = reportData?.documentMetadata?.summary;
  const byStatus = summary?.by_status || {};
  const filtersData = reportData?.filtersData || {};

  // Timeline click — navigate to the selected report (updates URL + state)
  const handleTimelineClick = (entry: typeof auditHistory[number]) => {
    if (entry.reportId === reportId) return;
    navigate(`/nii/audit-reports/run/${entry.runId}/${entry.reportId}`, {
      state: { auditName: entry.runName, nodeName, auditResult: entry.auditResult, gplVersion },
      replace: true
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch('');
    setGplStatus('');
    setCategory('');
    setMoClass('');
    setPage(1);
  };

  // Sort handler
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <i className="icon icon-sorting" />;
    return sortOrder === 'ASC'
      ? <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }} />
      : <i className="icon icon-sort-down" />;
  };

  const filtersApplied = [gplStatus, category, moClass, search].filter(Boolean).length;

  // Tree view data — group accumulated findings by mo_class_name → sub-group by parent DN
  const treeData = useMemo(() => {
    if (viewMode !== 'tree' || treeFindings.length === 0) return [];

    const moClassMap = new Map<string, GplFinding[]>();
    for (const finding of treeFindings) {
      const key = finding.mo_class_name;
      if (!moClassMap.has(key)) moClassMap.set(key, []);
      moClassMap.get(key)!.push(finding);
    }

    return Array.from(moClassMap.entries()).map(([moClassName, classFindings]) => {
      const subGroupMap = new Map<string, GplFinding[]>();
      for (const f of classFindings) {
        const parts = f.matched_dn.split(',');
        const parentLabel = parts.length > 1
          ? parts[parts.length - 2].split('=')[0]
          : moClassName;
        if (!subGroupMap.has(parentLabel)) subGroupMap.set(parentLabel, []);
        subGroupMap.get(parentLabel)!.push(f);
      }

      return {
        moClassName,
        children: Array.from(subGroupMap.entries()).map(([label, groupFindings]) => ({
          label,
          count: groupFindings.length,
          findings: groupFindings
        }))
      };
    });
  }, [viewMode, treeFindings]);

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      {/* Node Information Tile */}
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper component={Tile} title={nodeName || 'Node Report'} id="node-info-tile">
          </ReactEDSWrapper>
        </div>
      </div>

      {/* Findings MultiPanelTile */}
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper
            component={MultiPanelTile}
            title="Findings"
            style={{ minHeight: '500px' }}
            leftPanelAction={
              <ReactEDSWrapper component={Tooltip} message="Toggle audit runs" pointer>
                <ReactEDSWrapper
                  component={PanelAction}
                  icon={leftPanelOpen ? 'sidemenu-left-close' : 'sidemenu-left-open'}
                  onClick={() => setLeftPanelOpen(!leftPanelOpen)}
                />
              </ReactEDSWrapper>
            }
            leftPanel={
              <ReactEDSWrapper component={Panel} title="Audit Runs" onClose={() => setLeftPanelOpen(false)}>
                {auditHistory.length === 0 ? (
                  <p style={{ color: '#878787', fontSize: '12px' }}>No previous audit runs found.</p>
                ) : (
                  <>
                    <ReactEDSWrapper component={Timeline} selectable headings collapsible>
                      {visibleHistory.map((entry) => (
                        <ReactEDSWrapper
                          key={entry.reportId}
                          component={Timeline.Event}
                          timestamp={new Date(entry.createdAt)}
                          active={entry.reportId === reportId}
                          icon="check"
                          onClick={() => handleTimelineClick(entry)}
                        >
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleTimelineClick(entry)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <strong>{entry.runName || '—'}</strong>
                              <ReactEDSWrapper component={Tag} color={entry.auditResult === 'compliant' ? 'green' : 'red'}>
                                {entry.auditResult === 'compliant' ? 'Compliant' : 'Non Compliant'}
                              </ReactEDSWrapper>
                            </div>
                            <span style={{ fontSize: '11px', color: '#878787', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#dc3545', display: 'inline-block' }} />
                              {entry.totalDiscrepancies || 0} Discrepancies
                            </span>
                          </div>
                        </ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                    {auditHistory.length > INITIAL_SHOW_COUNT && (
                      <div style={{ textAlign: 'center', marginTop: '12px' }}>
                        <span
                          style={{ color: '#0069c2', cursor: 'pointer', fontSize: '13px' }}
                          onClick={() => setShowAll(!showAll)}
                        >
                        {showAll ? 'Show Less' : 'Show Older'} {showAll ? <i className="icon icon-chevron-up" /> : <i className="icon icon-chevron-down" />}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </ReactEDSWrapper>
            }
            leftPanelOpen={leftPanelOpen}
          >
                {/* Summary counters */}
    
                <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Mismatch</span>
                    <span style={{ fontSize: '20px', fontWeight: 600, color: '#dc3545' }}>{byStatus['mismatch'] || 0}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Param Cleared</span>
                    <span style={{ fontSize: '20px', fontWeight: 600 }}>{byStatus['param_cleared'] || 0}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Param Set</span>
                    <span style={{ fontSize: '20px', fontWeight: 600, color: '#fd7e14' }}>{byStatus['param_set_new'] || 0}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Feature Activate</span>
                    <span style={{ fontSize: '20px', fontWeight: 600 }}>{byStatus['feature_activate'] || 0}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Feature Deactivate</span>
                    <span style={{ fontSize: '20px', fontWeight: 600 }}>{byStatus['feature_deactivate'] || 0}</span>
                  </div>
                </div>

                {/* Filters row */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Search By</span>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="input"
                        placeholder="Type here"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        style={{ width: '180px', paddingRight: '28px' }}
                      />
                      <i className="icon icon-search" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.6 }} />
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Status</span>
                    <ReactEDSWrapper
                      component={Select}
                      type="single"
                      label="Select option"
                      value={gplStatus}
                      onChange={(v: unknown) => { const val = (v as { target?: { value?: string } })?.target?.value ?? v; setGplStatus(String(val || '')); setPage(1); }}
                    >
                      {(filtersData['status'] || []).map((s: string) => (
                        <ReactEDSWrapper key={s} component={MenuItem} value={s}>{s}</ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Category</span>
                    <ReactEDSWrapper
                      component={Select}
                      type="single"
                      label="Select option"
                      value={category}
                      onChange={(v: unknown) => { const val = (v as { target?: { value?: string } })?.target?.value ?? v; setCategory(String(val || '')); setPage(1); }}
                    >
                      {(filtersData['category'] || []).map((s: string) => (
                        <ReactEDSWrapper key={s} component={MenuItem} value={s}>{s}</ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>MO Class</span>
                    <ReactEDSWrapper
                      component={Select}
                      type="single"
                      label="Select option"
                      value={moClass}
                      onChange={(v: unknown) => { const val = (v as { target?: { value?: string } })?.target?.value ?? v; setMoClass(String(val || '')); setPage(1); }}
                    >
                      {(filtersData['mo_class_name'] || []).map((s: string) => (
                        <ReactEDSWrapper key={s} component={MenuItem} value={s}>{s}</ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                  </div>
                </div>

                {/* Item count + clear filters + view toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#878787' }}>
                    {total} items{filtersApplied > 0 ? ` · ${filtersApplied} filter${filtersApplied > 1 ? 's' : ''} applied` : ''}
                    {filtersApplied > 0 && (
                      <span style={{ marginLeft: '8px', color: '#0069c2', cursor: 'pointer' }} onClick={clearFilters}>
                        Clear all filters
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <i
                      className="icon icon-view-list"
                      style={{ cursor: 'pointer', opacity: viewMode === 'table' ? 1 : 0.4, fontSize: '18px' }}
                      onClick={() => setViewMode('table')}
                    />
                    <i
                      className="icon icon-tree-subcategories"
                      style={{ cursor: 'pointer', opacity: viewMode === 'tree' ? 1 : 0.4, fontSize: '18px' }}
                      onClick={() => setViewMode('tree')}
                    />
                  </div>
                </div>

                {/* Loading */}
                {isLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                    <div className="loader small" />
                    <p>Loading findings…</p>
                  </div>
                )}

                {/* Table View */}
                {!isLoading && viewMode === 'table' && (
                  <>
                    <div className="table">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('status')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Status {getSortIcon('status')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('category')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Category {getSortIcon('category')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('mo_class_name')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>MO Class {getSortIcon('mo_class_name')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('parameter_name')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Attribute {getSortIcon('parameter_name')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('gpl_value')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Baseline Value {getSortIcon('gpl_value')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('node_value')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Current Value {getSortIcon('node_value')}</div>
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }} onClick={() => handleSort('matched_dn')}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>MO Instance (DN) {getSortIcon('matched_dn')}</div>
                            </th>
                            <th style={{ textAlign: 'right', padding: '10px 12px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {findings.length === 0 ? (
                            <tr>
                              <td colSpan={8} style={{ textAlign: 'center', padding: '32px' }}>
                                <i className="icon icon-info" style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }} />
                                No findings
                              </td>
                            </tr>
                          ) : (
                            findings.map((finding: GplFinding, idx: number) => {
                              const tag = STATUS_TAG[finding.status] || { label: finding.status, color: 'grey' };
                              return (
                                <tr
                                  key={idx}
                                  style={{ transition: 'background-color 0.2s' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                                >
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>
                                    <ReactEDSWrapper component={Tag} color={tag.color}>{tag.label}</ReactEDSWrapper>
                                  </td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>{finding.category}</td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>{finding.mo_class_name}</td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>{finding.parameter_name}</td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>{finding.gpl_value}</td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}` }}>{finding.node_value}</td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}`, maxWidth: '200px' }}>
                                    <span title={finding.matched_dn} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{finding.matched_dn}</span>
                                  </td>
                                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${!theme ? '#404040' : '#e0e0e0'}`, textAlign: 'right' }}>
                                    <ReactEDSWrapper component={Button} onClick={() => setSelectedFinding(finding)}>
                                      View Details
                                    </ReactEDSWrapper>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 0 && (
                      <ReactEDSWrapper
                        component={Pagination}
                        numPages={totalPages}
                        selectedPage={page}
                        onPaginationChangePage={(p: number) => setPage(p)}
                        optionsList={[
                          { value: '6', label: '6', active: limit === 6 },
                          { value: '10', label: '10', active: limit === 10 },
                          { value: '20', label: '20', active: limit === 20 },
                          { value: '50', label: '50', active: limit === 50 }
                        ]}
                        onPaginationChangeSelect={(size: string) => { setLimit(Number(size)); setPage(1); }}
                        withActions
                      />
                    )}
                  </>
                )}

                {/* Tree View */}
                {!isLoading && viewMode === 'tree' && (
                  treeFindings.length === 0 && !treeInitialData ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                      <div className="loader small" />
                      <p>Loading tree…</p>
                    </div>
                  ) : treeFindings.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#878787' }}>
                      <i className="icon icon-info" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
                      No findings
                    </div>
                  ) : (
                    <>
                    <ReactEDSWrapper component={Tree} key={`tree-${treeFindings.length}`}>
                      {treeData.map((node) => (
                        <ReactEDSWrapper
                          key={node.moClassName}
                          component={TreeItem}
                          title={node.moClassName}
                          id={`tree-${node.moClassName}`}
                          opened
                        >
                          {node.children.map((group, gIdx) => (
                            <ReactEDSWrapper
                              key={`${node.moClassName}-${gIdx}`}
                              component={TreeItem}
                              title={`${group.label} • ${group.count}`}
                              id={`tree-${node.moClassName}-${gIdx}`}
                              opened={gIdx === 0}
                            >
                              {group.findings.map((finding, fIdx) => (
                                <li
                                  key={fIdx}
                                  onClick={() => setSelectedFinding(finding)}
                                  style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: '40px', padding: '6px 0 6px 60px', cursor: 'pointer', transition: 'background-color 0.2s' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                                >
                                  <span>
                                    <ReactEDSWrapper component={Tag} color={(STATUS_TAG[finding.status] || { color: 'grey' }).color}>
                                      {(STATUS_TAG[finding.status] || { label: finding.status }).label}
                                    </ReactEDSWrapper>
                                  </span>
                                  <span style={{ whiteSpace: 'nowrap' }}>{finding.parameter_name}</span>
                                </li>
                              ))}
                            </ReactEDSWrapper>
                          ))}
                        </ReactEDSWrapper>
                      ))}
                    </ReactEDSWrapper>
                    {treeFindings.length < treeTotal && (
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <span
                          style={{ color: '#0069c2', cursor: 'pointer', fontSize: '13px' }}
                          onClick={!treeLoadingMore ? handleShowMoreTree : undefined}
                        >
                          {treeLoadingMore ? 'Loading...' : `Show More (${treeFindings.length} of ${treeTotal} loaded)`}
                        </span>
                      </div>
                    )}
                    </>
                  )
                )}
          </ReactEDSWrapper>
        </div>
      </div>

      {/* Finding Detail Dialog */}
      {selectedFinding && (
        <ReactEDSWrapper
          component={Dialog}
          show={!!selectedFinding}
          onClose={() => setSelectedFinding(null)}
          title="Finding Details"
          closeButtonLabel="Close"
          bottom={<span></span>}
        >
          <div style={{ padding: '8px 0' }}>
            {/* Metadata row */}
            <div style={{ display: 'flex', gap: '32px', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>MO Class</span>
                <span>{selectedFinding.mo_class_name}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Parameter</span>
                <span>{selectedFinding.parameter_name}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Status</span>
                <ReactEDSWrapper component={Tag} color={(STATUS_TAG[selectedFinding.status] || { color: 'grey' }).color}>
                  {(STATUS_TAG[selectedFinding.status] || { label: selectedFinding.status }).label}
                </ReactEDSWrapper>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Category</span>
                <span>{selectedFinding.category}</span>
              </div>
            </div>

            {/* Values comparison */}
            <div style={{ display: 'flex', gap: '48px', marginBottom: '20px' }}>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>GPL Audit</strong>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Target Value</span>
                <span>{selectedFinding.gpl_value || '—'}</span>
              </div>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>Current Node State</strong>
                <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>Current Value</span>
                <span>{selectedFinding.node_value || '—'}</span>
              </div>
            </div>

            {/* MO Instance (DN) */}
            <div style={{ marginBottom: '20px' }}>
              <span style={{ fontSize: '11px', color: '#878787', display: 'block' }}>MO Instance (DN)</span>
              <span style={{ wordBreak: 'break-all' }}>{selectedFinding.matched_dn || '—'}</span>
            </div>
          </div>
        </ReactEDSWrapper>
      )}
    </div>
  );
};

export default GplNodeReport;
